import {
  Button,
  FormControl,
  FormLabel,
  Paper,
  Stack,
  TableBody,
  TableContainer,
} from '@mui/material';
import { CalculsFormCalendrierDepense } from '@norma/shared/constants/calculs';
import { defaultTableDepenseRow } from '@norma/shared/constants/prejudice/defaultValues/FormCalendrierDepense.defaultValues';
import { Bareme } from '@norma/shared/types/bareme.type';
import { MonetaryErosion } from '@norma/shared/types/monetaryErosion.type';
import {
  capitalisationDateAttributionTypes,
  DepenseType,
  NumeroPieceValuesRow,
  PrejudiceFormCalendrierDepense,
  PrejudiceFormCalendrierDepenseCapitalisation,
  PrejudiceFormCalendrierDepenseCapitalisationRow,
  PrejudiceType,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import { VictimeIndirecte } from '@norma/shared/types/victimeIndirecte.type';
import React, { memo, useMemo, useRef } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalculsBox } from 'src/components/basic/CalculsBox';
import { RadioFormField } from 'src/components/forms/RadioFieldForm';
import { getDefaultMinAndMaxDate } from 'src/constants/prejudice/validation';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import { NormalTable } from '../../../styled';
import { SelectBareme } from '../Capitalisation/SelectBareme';
import { TableDepensesFooter } from './TableDepensesFooter';
import { TableDepensesHeader } from './TableDepensesHeader';
import { TableDepensesRow } from './TableDepensesRow';

interface Props {
  control:
    | Control<PrejudiceFormCalendrierDepenseCapitalisation>
    | Control<PrejudiceFormCalendrierDepense>;
  tiersPayeurs: string[];
  isCalculCapitalisation?: boolean;
  dateConsolidation: Date | undefined;
  dateLiquidation?: Date | undefined;
  monetaryErosions: MonetaryErosion[];
  hideDepenseRecurrente?: boolean;
  hideFrequenceViagere?: boolean;
  hidePeriode?: boolean;
  victimesIndirectes?: VictimeIndirecte[];
  isVictimeIndirecte?: boolean;
  procedure: Procedure;
  victime: Victime;
  prejudiceType: PrejudiceType;
  baremesCapitalisation?: Bareme[];
  allNumerosPieces: NumeroPieceValuesRow[];
  shouldNotDisplayCapitalisation?: boolean;
  dateDeces: Date | undefined;
}

const TableDepensesComponent: React.FC<Props> = ({
  control,
  tiersPayeurs,
  isCalculCapitalisation,
  dateLiquidation,
  monetaryErosions,
  hideDepenseRecurrente,
  hideFrequenceViagere,
  isVictimeIndirecte,
  victimesIndirectes,
  hidePeriode,
  procedure,
  victime,
  prejudiceType,
  shouldNotDisplayCapitalisation,
  dateDeces,
  ...props
}) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const { duplicate, remove, append, fields } = useFormArrayUtils({
    control: control as Control<PrejudiceFormCalendrierDepenseCapitalisation>,
    name: 'rows',
    getValues,
    setValue,
  });
  const tableRef = useRef<HTMLTableElement>(null);

  const onAddRow = (type: DepenseType) => {
    const dateAttributionType = getValues?.(
      'capitalisation.dateAttributionType',
    );
    append(
      defaultTableDepenseRow({
        hidePeriode,
        tiersPayeurs,
        isCalculCapitalisation,
        type,
        procedure,
        victime,
        dateAttributionType,
      }) as PrejudiceFormCalendrierDepenseCapitalisationRow,
    );
    if (tableRef.current) {
      tableRef.current.scrollIntoView({
        block: 'end',
        behavior: 'smooth',
      });
    }
  };

  const handleDuplicate = (index: number) => {
    duplicate(index);
    if (tableRef.current) {
      tableRef.current.scrollIntoView({
        block: 'end',
        behavior: 'smooth',
      });
    }
  };

  const {
    minDate: { date: minDate },
    maxDate: { date: maxDate },
  } = useMemo(
    () =>
      getDefaultMinAndMaxDate({
        procedure,
        victime,
        prejudiceType,
      }),
    [procedure, victime, prejudiceType],
  );
  const {
    minDate: { date: minDateJustificatif },
    maxDate: { date: maxDateJustificatif },
  } = useMemo(
    () =>
      CalculsFormCalendrierDepense.getMinAndMaxDateJustificatif({
        procedure,
        isCalculCapitalisation,
      }),
    [procedure, victime, isCalculCapitalisation],
  );

  return (
    <>
      <Stack
        alignItems="start"
        width="100%"
        direction="row"
        spacing={2}
        height={100}
      >
        <CalculsBox padding={2} height="100%">
          <FormControl>
            <FormLabel
              sx={{
                textAlign: 'center',
              }}
            >
              {t('prejudice.revalorisation.coefficientsType.choice.label')}
            </FormLabel>
            <RadioFormField
              control={control as Control<PrejudiceFormCalendrierDepense>}
              name="revalorisationCoefficientsType"
              options={[
                {
                  label: t(
                    'prejudice.revalorisation.coefficientsType.choice.options.mensuel',
                  ),
                  value: 'mensuel',
                },
                {
                  label: t(
                    'prejudice.revalorisation.coefficientsType.choice.options.annuel',
                  ),
                  value: 'annuel',
                },
              ]}
              row={true}
            />
          </FormControl>
        </CalculsBox>
        {isCalculCapitalisation && !shouldNotDisplayCapitalisation ? (
          <>
            <CalculsBox
              padding={2}
              display="flex"
              height="100%"
              justifyContent="center"
            >
              <SelectBareme
                control={
                  control as Control<PrejudiceFormCalendrierDepenseCapitalisation>
                }
                baremes={props.baremesCapitalisation ?? []}
                name="capitalisation.bareme"
              />
            </CalculsBox>
            {/* <CalculsBox
              alignItems="center"
              padding={2}
              height="100%"
              display="flex"
            >
              <FormControlLabel
                label={t(
                  'prejudice.capitalisation.form.fields.enableCapitalisationDifferee.label',
                )}
                labelPlacement="start"
                control={
                  <CheckboxFormField
                    id="capitalisation.enableCapitalisationDifferee"
                    control={
                      control as Control<PrejudiceFormCalendrierDepenseCapitalisation>
                    }
                    name="capitalisation.enableCapitalisationDifferee"
                  />
                }
              />
              <Tooltip
                title={
                  <Typography
                    whiteSpace="pre-line"
                    maxHeight="400px"
                    overflow="auto"
                  >
                    {t(
                      'prejudice.capitalisation.form.fields.enableCapitalisationDifferee.tooltip',
                    )}
                  </Typography>
                }
              >
                <IconButton>
                  <Help />
                </IconButton>
              </Tooltip>
            </CalculsBox> */}
            <CalculsBox
              padding={2}
              display="flex"
              height="100%"
              flexDirection="column"
            >
              <FormControl>
                <FormLabel>
                  {t(
                    'prejudice.prejudicesFormTypes.CALENDRIER_DEPENSE_CAPITALISATION.fields.rows.capitalisation.dateAttribution.title',
                  )}
                </FormLabel>
                <RadioFormField
                  control={
                    control as Control<PrejudiceFormCalendrierDepenseCapitalisation>
                  }
                  name="capitalisation.dateAttributionType"
                  options={capitalisationDateAttributionTypes.map(
                    (dateAttributionType, index) => ({
                      label: t(
                        `prejudice.prejudicesFormTypes.CALENDRIER_DEPENSE_CAPITALISATION.fields.rows.capitalisation.dateAttribution.${dateAttributionType}`,
                      ),
                      value: capitalisationDateAttributionTypes[index],
                    }),
                  )}
                  row={true}
                />
              </FormControl>
            </CalculsBox>
          </>
        ) : null}
      </Stack>
      <TableContainer component={Paper} sx={{ maxHeight: '570px' }}>
        <NormalTable size="medium" stickyHeader ref={tableRef}>
          <TableDepensesHeader
            hideDepenseRecurrente={hideDepenseRecurrente}
            hidePeriode={hidePeriode}
            isVictimeIndirecte={isVictimeIndirecte}
            tiersPayeurs={tiersPayeurs}
            isCalculCapitalisation={isCalculCapitalisation}
            shouldNotDisplayCapitalisation={
              shouldNotDisplayCapitalisation ?? false
            }
          />
          <TableBody>
            {fields.map((field, index) => {
              return (
                <TableDepensesRow
                  key={field.id}
                  control={control}
                  tiersPayeurs={procedure.tiersPayeurs}
                  monetaryErosions={monetaryErosions}
                  dateLiquidation={dateLiquidation}
                  hideDepenseRecurrente={hideDepenseRecurrente}
                  hideFrequenceViagere={hideFrequenceViagere}
                  hidePeriode={hidePeriode}
                  isVictimeIndirecte={isVictimeIndirecte}
                  victimesIndirectes={victimesIndirectes}
                  procedure={procedure}
                  victime={victime}
                  isCalculCapitalisation={isCalculCapitalisation}
                  rowIndex={index}
                  fieldId={field.id}
                  fieldType={field.type}
                  onClickRemove={remove}
                  onClickDuplicate={handleDuplicate}
                  minDate={minDate}
                  maxDate={maxDate}
                  minDateJustificatif={minDateJustificatif}
                  maxDateJustificatif={maxDateJustificatif}
                  prejudiceType={prejudiceType}
                  shouldNotDisplayCapitalisation={
                    shouldNotDisplayCapitalisation ?? false
                  }
                  dateDeces={dateDeces}
                  {...props}
                />
              );
            })}
          </TableBody>
          <TableDepensesFooter
            control={control}
            hideDepenseRecurrente={hideDepenseRecurrente}
            hidePeriode={hidePeriode}
            isCalculCapitalisation={isCalculCapitalisation}
            tiersPayeurs={tiersPayeurs}
            dateLiquidation={dateLiquidation}
            monetaryErosions={monetaryErosions}
            isVictimeIndirecte={isVictimeIndirecte}
            shouldNotDisplayCapitalisation={
              shouldNotDisplayCapitalisation ?? false
            }
          />
        </NormalTable>
      </TableContainer>
      <Stack
        direction="row"
        spacing={4}
        alignSelf="center"
        width="100%"
        justifyContent="center"
      >
        <Button
          onClick={() => {
            onAddRow('ponctuelle');
          }}
        >
          {t(
            'prejudice.prejudicesFormTypes.CALENDRIER_DEPENSE.table.addDepensePonctuelleRow',
          )}
        </Button>
        {!hideDepenseRecurrente && (
          <Button onClick={() => onAddRow('recurrente')}>
            {t(
              'prejudice.prejudicesFormTypes.CALENDRIER_DEPENSE.table.addDepenseRecurrenteRow',
            )}
          </Button>
        )}
      </Stack>
    </>
  );
};

export const TableDepenses = memo(TableDepensesComponent);
